import { emailConfig } from '../config/email';
import { toast } from 'react-hot-toast';
import { supabase } from './supabase';
import axios from 'axios';

// Function to send a real email using Gmail SMTP
export const sendTestEmail = async (subject: string, emailContent: string): Promise<boolean> => {
  try {
    // Send the actual email using Gmail SMTP
    const response = await axios.post('https://api.sendgrid.com/v3/mail/send', {
      personalizations: [{
        to: [{ email: emailConfig.senderEmail }]
      }],
      from: { email: emailConfig.senderEmail, name: emailConfig.senderName },
      subject: `[TEST] ${subject}`,
      content: [{
        type: 'text/html',
        value: emailContent
      }]
    }, {
      headers: {
        'Authorization': `Bearer ${process.env.SENDGRID_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status !== 202) {
      throw new Error('Failed to send email');
    }
    
    // Record the test email in the database
    const { error } = await supabase
      .from('sent_emails')
      .insert([
        {
          email: emailConfig.senderEmail,
          subject: `[TEST] ${subject}`,
          campaign: 'Test Email',
          sent_date: new Date().toISOString()
        }
      ]);
    
    if (error) {
      console.error('Error recording test email:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error in test email:', error);
    return false;
  }
};

// Function to check if an email has been sent before
export const checkPreviouslySentEmail = async (email: string): Promise<{ sent: boolean, date?: string }> => {
  try {
    if (!email) {
      throw new Error('Email address is required');
    }

    const { data, error } = await supabase
      .from('sent_emails')
      .select('email, sent_date')
      .eq('email', email)
      .order('sent_date', { ascending: false })
      .limit(1);
    
    if (error) {
      console.error('Error checking sent email:', error);
      toast.error('Failed to check email history');
      return { sent: false };
    }
    
    if (data && data.length > 0) {
      return { 
        sent: true, 
        date: new Date(data[0].sent_date).toLocaleDateString() 
      };
    }
    
    return { sent: false };
  } catch (error) {
    console.error('Error checking sent email:', error);
    return { sent: false };
  }
};

// Function to record a sent email
export const recordSentEmail = async (email: string, subject: string, campaign: string): Promise<boolean> => {
  try {
    if (!email || !subject || !campaign) {
      throw new Error('Missing required fields for recording sent email');
    }

    const sentDate = new Date().toISOString();
    
    const { error } = await supabase
      .from('sent_emails')
      .insert([
        { 
          email, 
          subject, 
          campaign,
          sent_date: sentDate 
        }
      ]);
    
    if (error) {
      console.error('Error recording sent email:', error);
      toast.error('Failed to record sent email in database');
      return false;
    }
    
    return true;
  } catch (error) {
    const err = error as Error;
    console.error('Error recording sent email:', err);
    toast.error(`Database error: ${err.message || 'Failed to record email'}`);
    return false;
  }
};

// Function to get all previously sent emails
export const getAllSentEmails = async (): Promise<{ email: string, sentDate: string }[]> => {
  try {
    const { data, error } = await supabase
      .from('sent_emails')
      .select('email, sent_date')
      .order('sent_date', { ascending: false });
    
    if (error) {
      console.error('Error getting sent emails:', error);
      return [];
    }
    
    return data.map(record => ({
      email: record.email,
      sentDate: new Date(record.sent_date).toLocaleDateString()
    }));
  } catch (error) {
    console.error('Error getting sent emails:', error);
    return [];
  }
};